import React from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import styles from '@utils/styles';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import { TrackClickAfterSearch } from '@rsc/utils/algoliaAnalytics';
import breakpoints from '@rsc/ui-components/breakpoints';
import ArrowIcon from '@components/svgs/arrow-icon.svg';

const ArticleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;

  a:hover {
    text-decoration: none;
  }

  a:hover h2 {
    text-decoration: underline;
  }

  @media (min-width: ${breakpoints.lg}) {
    margin-bottom: 45px;
  }
`;

const ArticleTitle = styled.h2`
  font-weight: ${styles.fontWeight.medium};
  margin: 0.5rem 0;
`;

const ArticleContent = styled.div`
  font-weight: ${styles.fontWeight.light};

  p {
    color: black;
  }

  @media (min-width: ${breakpoints.lg}) {
    min-height: 120px;
  }

  &:hover {
    text-decoration: none;

    svg {
      fill: ${styles.colors.blue[700]};
      stroke: ${styles.colors.blue[700]};
    }
  }

  svg {
    height: 18px;
    fill: #303539;
    stroke: #303539;
    transition: all 0.3s ease;
    vertical-align: middle;
  }
`;

const ArticleImage = styled.img`
  width: 100%;
  height: auto;
  @media (min-width: ${breakpoints.md}) {
    height: 264px;
    object-fit: cover;
  }
`;

const ReadMore = styled(Link)`
  color: ${styles.colors.gray[700]};
  font-weight: ${styles.fontWeight.semiBold};
  text-decoration: none;
  transition: color 0.3s ease;
  margin-top: auto;

  &:hover {
    color: ${styles.colors.orange[500]};

    svg {
      fill: ${styles.colors.orange[500]};
      stroke: ${styles.colors.orange[500]};
    }
  }

  svg {
    height: 18px;
    fill: #303539;
    stroke: #303539;
    transition: all 0.3s ease;
    vertical-align: middle;
  }
`;

const Article = ({ article }) => (
  <ArticleWrapper>
    <Link
      to={`/blog/${article.handle}`}
      aria-label={article.title}
      onClick={() => {
        TrackClickAfterSearch(article?.insights);
      }}
    >
      <ArticleImage
        src={`${article.image.url || article.image.src}`}
        alt={article.image.altText || article.title}
      />

      <ArticleTitle>{article.title}</ArticleTitle>
      <ArticleContent>
        <p>{parse(article.excerpt)}</p>
      </ArticleContent>
    </Link>

    {article?.readMore && (
      <ReadMore to={`/blog/${article.handle}`} aria-label={article.readMore}>
        {article.readMore}
        <ArrowIcon transform="rotate(270)" />
      </ReadMore>
    )}
  </ArticleWrapper>
);

Article.propTypes = {
  article: PropTypes.shape({
    title: PropTypes.string.isRequired,
    handle: PropTypes.string,
    excerpt: PropTypes.string.isRequired,
    image: PropTypes.shape({
      altText: PropTypes.string,
      url: PropTypes.string,
      src: PropTypes.string,
      localFile: PropTypes.shape({
        childImageSharp: PropTypes.shape({}),
      }),
    }),
    readMore: PropTypes.string,
    insights: PropTypes.func,
  }).isRequired,
};

export default Article;
